// The building blocks of the dashboard and the admin back-office, on Tailwind (theme.css). Accessible by
// default: labels bound to inputs, errors announced (aria-describedby, role="alert"), dialogs on the native
// <dialog> element. An app that uses them adds `@source` for this folder to its stylesheet, so that Tailwind
// generates their classes.

import { type ButtonHTMLAttributes, type CSSProperties, type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes,
  type TextareaHTMLAttributes, useEffect, useId, useRef, useState } from 'react';
import { Link, type LinkProps } from 'react-router';

const cx = (...c: (string | false | null | undefined)[]) => c.filter(Boolean).join(' ');

type Variant = 'primary' | 'secondary' | 'danger' | 'ghost';

const buttonClass = (variant: Variant, size: 'sm' | 'md', className?: string) => cx(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md font-medium transition-colors disabled:opacity-50',
  size === 'sm' ? 'px-2.5 py-1 text-sm' : 'px-3.5 py-2 text-sm',
  variant === 'primary' && 'bg-brand-600 text-on-brand hover:bg-brand-700',
  variant === 'secondary' && 'border border-slate-300 bg-white text-slate-800 hover:bg-slate-50',
  variant === 'danger' && 'bg-red-600 text-on-color hover:bg-red-700',
  variant === 'ghost' && 'text-slate-700 hover:bg-slate-100',
  className);

export function Button({ variant = 'primary', size = 'md', loading = false, className, children, disabled, ...rest }:
  ButtonHTMLAttributes<HTMLButtonElement> & { variant?: Variant; size?: 'sm' | 'md'; loading?: boolean }) {
  return (
    <button {...rest} disabled={disabled || loading} aria-busy={loading || undefined} className={buttonClass(variant, size, className)}>
      {loading && <Spinner small />}
      {children}
    </button>
  );
}

/** A link that looks like a button (a <button> inside a link would be invalid, and read twice). */
export function LinkButton({ variant = 'primary', size = 'md', className, ...rest }:
  LinkProps & { variant?: Variant; size?: 'sm' | 'md' }) {
  return <Link {...rest} className={buttonClass(variant, size, className)} />;
}

export function Spinner({ small = false, label }: { small?: boolean; label?: string }) {
  return (
    <span role="status" className="inline-flex items-center gap-2 text-slate-500">
      <span className={cx('animate-spin rounded-full border-2 border-current border-t-transparent', small ? 'h-3.5 w-3.5' : 'h-5 w-5')} />
      {label && <span className="text-sm">{label}</span>}
    </span>
  );
}

interface FieldProps {
  label: ReactNode;
  hint?: ReactNode;
  error?: string | undefined;
  children: (props: { id: string; 'aria-invalid'?: boolean; 'aria-describedby'?: string }) => ReactNode;
  className?: string;
}

/** A labelled control with its hint and error. */
export function Field({ label, hint, error, children, className }: FieldProps) {
  const id = useId();
  const described = [hint ? `${id}-hint` : null, error ? `${id}-error` : null].filter(Boolean).join(' ');
  return (
    <div className={cx('flex flex-col gap-1', className)}>
      <label htmlFor={id} className="text-sm font-medium text-slate-700">{label}</label>
      {children({ id, ...(error ? { 'aria-invalid': true } : {}), ...(described ? { 'aria-describedby': described } : {}) })}
      {hint && !error && <p id={`${id}-hint`} className="text-xs text-slate-500">{hint}</p>}
      {error && <p id={`${id}-error`} role="alert" className="text-xs text-red-700">{error}</p>}
    </div>
  );
}

const controlBase = 'rounded-md border border-slate-300 bg-white px-3 py-2 text-sm shadow-xs '
  + 'focus:border-brand-500 aria-[invalid=true]:border-red-500 disabled:bg-slate-100';

/** Full width unless the caller gives a width (two width utilities would compete by stylesheet order). */
const control = (className?: string) => cx(controlBase, /(^|\s)(max-)?w-/.test(className ?? '') ? null : 'w-full', className);

export function Input({ className, ...rest }: InputHTMLAttributes<HTMLInputElement>) {
  return <input {...rest} className={control(className)} />;
}

export function Textarea({ className, ...rest }: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return <textarea rows={3} {...rest} className={control(className)} />;
}

export function Select({ className, children, ...rest }: SelectHTMLAttributes<HTMLSelectElement>) {
  return <select {...rest} className={control(cx('pr-8', className))}>{children}</select>;
}

export function Checkbox({ label, ...rest }: InputHTMLAttributes<HTMLInputElement> & { label: ReactNode }) {
  const id = useId();
  return (
    <div className="flex items-start gap-2">
      <input id={id} type="checkbox" {...rest} className="mt-0.5 h-4 w-4 rounded border-slate-300 text-brand-600" />
      <label htmlFor={id} className="text-sm text-slate-700">{label}</label>
    </div>
  );
}

export function Card({ title, actions, children, className }: { title?: ReactNode; actions?: ReactNode; children: ReactNode;
  className?: string }) {
  return (
    <section className={cx('rounded-lg border border-slate-200 bg-white shadow-xs', className)}>
      {(title || actions) && (
        <header className="flex items-center justify-between gap-3 border-b border-slate-100 px-4 py-3">
          {title && <h2 className="text-sm font-semibold text-slate-800">{title}</h2>}
          {actions && <div className="flex items-center gap-2">{actions}</div>}
        </header>
      )}
      <div className="p-4">{children}</div>
    </section>
  );
}

export function PageHeader({ title, subtitle, actions }: { title: ReactNode; subtitle?: ReactNode; actions?: ReactNode }) {
  return (
    <div className="mb-5 flex flex-wrap items-end justify-between gap-3">
      <div>
        <h1 className="text-xl font-semibold text-slate-900">{title}</h1>
        {subtitle && <p className="mt-0.5 text-sm text-slate-500">{subtitle}</p>}
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </div>
  );
}

type Tone = 'red' | 'amber' | 'green' | 'slate' | 'violet' | 'blue';

const TONES: Record<Tone, string> = {
  red: 'bg-red-50 text-red-800 ring-red-200', amber: 'bg-amber-50 text-amber-800 ring-amber-200',
  green: 'bg-emerald-50 text-emerald-800 ring-emerald-200', slate: 'bg-slate-100 text-slate-700 ring-slate-200',
  violet: 'bg-violet-50 text-violet-800 ring-violet-200', blue: 'bg-brand-50 text-brand-700 ring-brand-100',
};

export function Badge({ tone = 'slate', children }: { tone?: Tone; children: ReactNode }) {
  return (
    <span className={cx('inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset', TONES[tone])}>
      {children}
    </span>
  );
}

export function Alert({ tone = 'blue', title, children, action }: { tone?: 'red' | 'amber' | 'blue' | 'green';
  title?: ReactNode; children?: ReactNode; action?: ReactNode }) {
  const styles = { red: 'border-red-200 bg-red-50 text-red-900', amber: 'border-amber-200 bg-amber-50 text-amber-900',
    blue: 'border-brand-100 bg-brand-50 text-brand-700', green: 'border-emerald-200 bg-emerald-50 text-emerald-900' };
  return (
    <div role={tone === 'red' ? 'alert' : 'status'} className={cx('flex items-start justify-between gap-3 rounded-md border px-4 py-3 text-sm', styles[tone])}>
      <div>
        {title && <p className="font-medium">{title}</p>}
        {children && <div className={title ? 'mt-0.5' : ''}>{children}</div>}
      </div>
      {action && <div className="shrink-0">{action}</div>}
    </div>
  );
}

export function EmptyState({ title, children, action }: { title: ReactNode; children?: ReactNode; action?: ReactNode }) {
  return (
    <div className="rounded-lg border border-dashed border-slate-300 bg-white px-6 py-10 text-center">
      <p className="font-medium text-slate-800">{title}</p>
      {children && <div className="mx-auto mt-1 max-w-md text-sm text-slate-500">{children}</div>}
      {action && <div className="mt-4 flex justify-center">{action}</div>}
    </div>
  );
}

/** A modal on the native <dialog>: focus is trapped, Escape and the × button close it. */
export function Modal({ open, title, onClose, children, footer, wide = false, closeLabel = 'Close' }: { open: boolean;
  title: ReactNode; onClose: () => void; children: ReactNode; footer?: ReactNode; wide?: boolean; closeLabel?: string }) {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (open && !d.open) d.showModal();
    if (!open && d.open) d.close();
  }, [open]);
  return (
    <dialog
      ref={ref}
      onClose={onClose}
      onCancel={(e) => { e.preventDefault(); onClose(); }}
      className={cx('m-auto w-[calc(100%-2rem)] rounded-lg bg-white p-0 text-slate-900 shadow-xl backdrop:bg-nuit/60', wide ? 'max-w-2xl' : 'max-w-md')}
    >
      {open && (
        <div>
          <header className="flex items-start justify-between gap-3 border-b border-slate-100 py-4 pl-5 pr-3">
            <h2 className="text-base font-semibold text-slate-900">{title}</h2>
            <button type="button" onClick={onClose} aria-label={closeLabel} title={closeLabel}
              className="-my-1 rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700">
              <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"
                className="h-5 w-5" aria-hidden="true"><path d="M5 5l10 10M15 5L5 15" /></svg>
            </button>
          </header>
          <div className="max-h-[70vh] overflow-y-auto px-5 py-4">{children}</div>
          {footer && <footer className="flex justify-end gap-2 border-t border-slate-100 px-5 py-3">{footer}</footer>}
        </div>
      )}
    </dialog>
  );
}

export function Table({ head, children, empty }: { head: ReactNode[]; children: ReactNode; empty?: ReactNode }) {
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-slate-200 text-sm">
        <thead>
          <tr>{head.map((h, i) => <th key={i} scope="col" className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">{h}</th>)}</tr>
        </thead>
        <tbody className="divide-y divide-slate-100">{children}</tbody>
      </table>
      {empty}
    </div>
  );
}

export function Td({ children, className }: { children?: ReactNode; className?: string }) {
  return <td className={cx('px-3 py-2 align-top text-slate-700', className)}>{children}</td>;
}

export { cx };

/** Maillon Froid's mark: two linked rings, as on the site. Its colours follow the theme (logo-a, logo-b). */
export function Logo({ className, wordmark, onDark = false }: { className?: string; wordmark?: ReactNode; onDark?: boolean }) {
  // On a surface that stays dark in both themes (the back-office's sidebar), the site's own colours.
  const fixed = onDark ? { '--color-logo-a': '#9fd4f2', '--color-logo-b': '#eaf4fa' } as CSSProperties : undefined;
  return (
    <span style={fixed} className={cx('inline-flex items-center gap-2.5 font-bold tracking-tight', onDark ? 'text-givre' : 'text-slate-900', className)}>
      <svg viewBox="0 0 74 26" aria-hidden="true" className="h-[0.9em] w-auto shrink-0">
        <rect x="2" y="2" width="40" height="22" rx="11" fill="none" stroke="var(--color-logo-a)" strokeWidth="4" />
        <rect x="32" y="2" width="40" height="22" rx="11" fill="none" stroke="var(--color-logo-b)" strokeWidth="4" />
      </svg>
      {wordmark ?? 'Maillon Froid'}
    </span>
  );
}

// Light and dark themes (theme.css). Without a choice the system's preference applies; a choice is remembered on
// this device and set as <html data-theme>.
export type Theme = 'light' | 'dark';
const THEME_KEY = 'cck-theme';
const THEME_EVENT = 'cck-theme';
const darkQuery = () => (typeof matchMedia === 'function' ? matchMedia('(prefers-color-scheme: dark)') : null);

function storedTheme(): Theme | null {
  try {
    const v = localStorage.getItem(THEME_KEY);
    return v === 'light' || v === 'dark' ? v : null;
  } catch { return null; }
}

export function currentTheme(): Theme {
  return storedTheme() ?? (darkQuery()?.matches ? 'dark' : 'light');
}

/** Applies the remembered choice: call once before the first render. */
export function applyStoredTheme(): void {
  const t = storedTheme();
  if (t) document.documentElement.dataset.theme = t;
}

export function setTheme(theme: Theme): void {
  try { localStorage.setItem(THEME_KEY, theme); } catch { /* private mode: for this page only */ }
  document.documentElement.dataset.theme = theme;
  window.dispatchEvent(new Event(THEME_EVENT));
}

/** The theme in use, updated when the person or their system changes it (charts redraw with it). */
export function useTheme(): Theme {
  const [theme, set] = useState<Theme>(currentTheme);
  useEffect(() => {
    const update = () => set(currentTheme());
    const q = darkQuery();
    window.addEventListener(THEME_EVENT, update);
    q?.addEventListener('change', update);
    return () => { window.removeEventListener(THEME_EVENT, update); q?.removeEventListener('change', update); };
  }, []);
  return theme;
}

/** A theme colour as a CSS value, for canvases (charts): e.g. themeColor('slate-500'). */
export function themeColor(name: string, fallback = '#5a7082'): string {
  if (typeof document === 'undefined') return fallback;
  return getComputedStyle(document.documentElement).getPropertyValue(`--color-${name}`).trim() || fallback;
}

/** Switches between the light and the dark theme. */
export function ThemeToggle({ labels, className, onDark = false }: { labels: { light: string; dark: string }; className?: string;
  onDark?: boolean }) {
  const theme = useTheme();
  const next: Theme = theme === 'dark' ? 'light' : 'dark';
  const label = next === 'dark' ? labels.dark : labels.light;
  return (
    <button type="button" onClick={() => setTheme(next)} aria-label={label} title={label}
      className={cx('inline-flex h-8 w-8 items-center justify-center rounded-md',
        onDark ? 'text-nuit-texte hover:bg-nuit-ligne hover:text-givre' : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900', className)}>
      {theme === 'dark' ? (
        <svg viewBox="0 0 24 24" aria-hidden="true" className="h-[18px] w-[18px]" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
          <circle cx="12" cy="12" r="4" />
          <path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
        </svg>
      ) : (
        <svg viewBox="0 0 24 24" aria-hidden="true" className="h-[18px] w-[18px]" fill="none" stroke="currentColor" strokeWidth="2" strokeLinejoin="round">
          <path d="M20 14.5A8 8 0 0 1 9.5 4a8 8 0 1 0 10.5 10.5Z" />
        </svg>
      )}
    </button>
  );
}
